/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useCallback, useEffect, useMemo, useState} from 'react'
import {MAIN_IMAGES_IMAGE_TYPE, MAX_B2C_ORDER_QUANTITY, MAX_D2B_ORDER_QUANTITY} from '../constants'
import useOcapiSite from './useOcapiSite'
import {useVariant} from './use-variant'
import {useIntl} from 'react-intl'
import {useVariationParams} from './use-variation-params'
import {useVariationAttributes} from './use-variation-attributes'
import {
    getMasterProductId,
    getProductQuantities,
    isProductEngravable,
    isProductOrderable,
    isProductSet
} from '../utils/product-utils'
import {useToast} from './use-toast'
import useEffectEvent from './use-effect-event'
import {useCurrentCustomer} from './use-current-customer'

const OUT_OF_STOCK = 'OUT_OF_STOCK'
const UNFULFILLABLE = 'UNFULFILLABLE'

// TODO: This needs to be refactored.
export const useDerivedProduct = (
    product,
    isProductPartOfSet = false,
    isProductPartOfBundle = false
) => {
    const toast = useToast()
    const {isD2BCustomer} = useCurrentCustomer().data
    const {productPurchaseLimit: maxB2COrderQuantity, d2bMaxQuantity: maxD2BOrderQuantity} =
        useOcapiSite().preferences
    const showLoading = !product
    const isProductABundle = product?.type?.bundle
    // use the site pref if defined and positive number, otherwise use the default
    const maxCartQuantity = isD2BCustomer
        ? maxD2BOrderQuantity > 0
            ? maxD2BOrderQuantity
            : MAX_D2B_ORDER_QUANTITY
        : maxB2COrderQuantity > 0
        ? maxB2COrderQuantity
        : MAX_B2C_ORDER_QUANTITY
    // ATS includes allocation + preorderBackorderAllocation
    const {stockLevel, stepQuantity, minOrderQuantity, maxOrderQuantity, initialQuantity} = useMemo(
        () => getProductQuantities(product, maxCartQuantity),
        [maxCartQuantity, product]
    )

    // used for product bundles when there are multiple products
    const lowestStockLevelProductName = product?.inventory?.lowestStockLevelProductName
    const intl = useIntl()
    const {arEnabled, arModelList, brightcoveEnabled, accountId, engravingImage} = useOcapiSite()
    const variant = useVariant(product, isProductPartOfSet, isProductPartOfBundle)
    const variationParams = useVariationParams(product, isProductPartOfSet, isProductPartOfBundle)
    const variationAttributes = useVariationAttributes(
        product,
        isProductPartOfSet,
        isProductPartOfBundle
    )
    const [quantity, setQuantity] = useState(initialQuantity)

    const onOverMaxOrderQuantity = useEffectEvent(() => {
        toast({
            title: isD2BCustomer
                ? intl.formatMessage({
                      defaultMessage:
                          'Maximum availability in stock. Please contact customer service to place an order for a higher quantity.',
                      id: 'product.alert.d2b.max_order_quantity'
                  })
                : intl.formatMessage({
                      defaultMessage: 'Maximum order quantity has been reached.',
                      id: 'product.alert.b2c.max_order_quantity'
                  }),
            status: 'error',
            duration: 5000
        })
    })
    const onChangeQuantity = useCallback(
        (_, value) => {
            if (value > maxOrderQuantity) {
                onOverMaxOrderQuantity()
                return false
            }
            setQuantity(value)
            return true
        },
        [maxOrderQuantity, onOverMaxOrderQuantity]
    )

    // A product is considered out of stock if the stock level is 0 or if we have all our
    // variation attributes selected, but don't have a variant. We do this because the API
    // will sometimes return all the variants even if they are out of stock, but for other
    // products it won't.
    const isOutOfStock =
        !stockLevel ||
        (!isProductABundle &&
            !variant &&
            Object.keys(variationParams).length === variationAttributes.length) ||
        (!isProductABundle && variant && !variant.orderable)
    const unfulfillable = stockLevel < quantity
    const inventoryMessages = {
        [OUT_OF_STOCK]: intl.formatMessage({
            defaultMessage: 'Out of stock',
            id: 'use_product.message.out_of_stock'
        }),
        [UNFULFILLABLE]: lowestStockLevelProductName
            ? intl.formatMessage(
                  {
                      defaultMessage: 'Only {stockLevel} left for {productName}!',
                      id: 'use_product.message.inventory_remaining_for_product'
                  },
                  {stockLevel, productName: lowestStockLevelProductName}
              )
            : intl.formatMessage(
                  {
                      defaultMessage: 'Only {stockLevel} left!',
                      id: 'use_product.message.inventory_remaining'
                  },
                  {stockLevel}
              )
    }

    // showInventoryMessage controls if add to cart button is disabled
    const showInventoryMessage = (variant || isProductABundle) && (isOutOfStock || unfulfillable)
    const inventoryMessage =
        (isOutOfStock && inventoryMessages[OUT_OF_STOCK]) ||
        (unfulfillable && inventoryMessages[UNFULFILLABLE])

    // If the product or `initialQuantity` changes, update the state. This typically happens
    // when either the master product changes, or the inventory of the product changes
    // from out-of-stock to in-stock or vice versa.
    // We use the master id as a key when variant or the product's id otherwise
    const productKey = getMasterProductId(product) || product?.id || product?.productId
    useEffect(() => {
        // skip updating when there is no product key - this might happen
        // during loading and we will have another chance to check for changes
        // once the product is loaded and we have a product key
        if (productKey) {
            setQuantity(initialQuantity)
        }
    }, [
        initialQuantity,
        // Note: keep the productKey in the dependencies to trigger the effect when the product changes
        productKey
    ])

    // parse product hotspots if available
    const hotspots = useMemo(() => {
        const relatedHotspotPids =
            product?.variants?.map((variant) => {
                return variant.productId
            }) || []
        if (product?.master?.masterId) {
            relatedHotspotPids.push(product?.master?.masterId)
        }

        if (product?.setProducts) {
            product.setProducts.forEach((setProduct) => {
                if (setProduct?.master?.masterId) {
                    relatedHotspotPids.push(setProduct?.master?.masterId)
                }
                if (setProduct?.variants) {
                    setProduct.variants.forEach((variant) => {
                        relatedHotspotPids.push(variant?.productId)
                    })
                }
            })
        }

        // remove hotspots related to master or any variant products
        const filteredHotspots = product?.c_hotspots || {}
        const mainImages = filteredHotspots?.[MAIN_IMAGES_IMAGE_TYPE] || {}
        Object.keys(mainImages).forEach((img) => {
            mainImages[img] = mainImages[img].reduce((result, hotspot) => {
                if (!relatedHotspotPids.includes(hotspot.pid)) {
                    result.push(hotspot)
                }
                return result
            }, [])
        })
        return filteredHotspots
    }, [product])

    const videos = useMemo(() => {
        if (brightcoveEnabled && product?.c_BrightcoveVideoProduct) {
            try {
                const videos = Object.values(JSON.parse(product?.c_BrightcoveVideoProduct))
                    .filter((video) => Object.keys(video).includes('videoId', 'playerId'))
                    .map((video) => {
                        return {
                            accountId,
                            type: 'brightcove',
                            ...video
                        }
                    })

                return videos
            } catch (e) {
                console.error('Error parsing product videos', e)
            }
        }
    }, [brightcoveEnabled, product?.c_BrightcoveVideoProduct, accountId])

    const ARModel = useMemo(() => {
        if (!arEnabled || arModelList == null) {
            return null
        }

        // The arModelList is retreived from the backend and it only
        // contains ids of product variants, not of master products.
        // If the current product is of type "master"
        // then we get the id of its first variant.
        let variantId
        if (product?.type?.variant) {
            variantId = product?.id
        } else if (product?.variants?.length) {
            variantId = product.variants[0]?.productId
        }

        return variantId in arModelList ? arModelList[variantId] : null
    }, [product, arModelList, arEnabled])

    const isSet = isProductSet(product)

    const configurator3DLink = product?.c_absConfiguratorLink
    const redirectLinkPartner = product?.c_redirectLinkPartner

    const isEngravable = useMemo(() => isProductEngravable(product), [product])

    const masterSubtypeTooltipText = product?.c_masterSubtypeTooltipText

    const isOrderable = useMemo(() => isProductOrderable(product), [product])

    const setIncludesBonusProduct = isSet && product?.c_bonus_product_included

    return {
        ARModel,
        videos,
        showLoading,
        showInventoryMessage,
        inventoryMessage,
        variationAttributes,
        quantity,
        minOrderQuantity,
        maxCartQuantity,
        maxOrderQuantity,
        stepQuantity,
        variationParams,
        setQuantity,
        variant,
        stockLevel,
        isOutOfStock,
        unfulfillable,
        hotspots,
        configurator3DLink,
        redirectLinkPartner,
        engravingImage,
        isEngravable,
        masterSubtypeTooltipText,
        isSet,
        isOrderable,
        setIncludesBonusProduct,
        onChangeQuantity
    }
}
